import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CalendarCheck, Calendar, Users, Clock, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-amber-500",
  confirmed: "bg-green-600",
  rejected: "bg-red-600",
  cancelled: "bg-muted text-muted-foreground",
};
const STATUS_LABEL = {
  pending: "Menunggu Konfirmasi",
  confirmed: "Dikonfirmasi",
  rejected: "Ditolak",
  cancelled: "Dibatalkan",
};

export const MyBookings = () => {
  const [items, setItems] = useState([]);

  const load = () => api.get("/bookings/my").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.patch(`/bookings/${id}/cancel`);
      toast.success("Booking dibatalkan");
      load();
    } catch { toast.error("Gagal batal"); }
  };

  return (
    <div data-testid="my-bookings">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">My Reservations</p>
        <h1 className="font-heading text-3xl font-black mt-2">Booking Saya</h1>
        <p className="text-muted-foreground mt-2">{items.length} booking</p>
      </div>

      {items.length === 0 ? (
        <div className="warm-card p-12 text-center">
          <CalendarCheck className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Belum ada booking. Cari restoran favorit kamu dan book meja!</p>
          <Link to="/restaurants"><Button className="rounded-full mt-4">Jelajahi Restoran</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(b => (
            <div key={b.id} className="warm-card p-4 flex gap-4" data-testid={`booking-${b.id}`}>
              {b.restaurant?.cover_image && (
                <img src={b.restaurant.cover_image} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <Link to={`/restaurants/${b.restaurant?.slug}`} className="font-heading font-bold hover:text-primary">{b.restaurant?.name}</Link>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-2">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{b.date}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{b.time}</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{b.guests} tamu</span>
                </div>
                {b.notes && <p className="text-xs text-muted-foreground mt-2 italic">"{b.notes}"</p>}
                {b.owner_note && (
                  <div className="text-xs mt-2 p-2 bg-secondary rounded">
                    <span className="font-bold">Owner: </span>{b.owner_note}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge className={STATUS_COLORS[b.status]} data-testid={`status-${b.id}`}>{STATUS_LABEL[b.status]}</Badge>
                {b.status === "pending" && (
                  <Button size="sm" variant="ghost" onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}>
                    <X className="w-3 h-3 mr-1" /> Batal
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const OwnerBookings = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/bookings/owner").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status, owner_note = "") => {
    try {
      await api.patch(`/bookings/${id}/status`, { status, owner_note });
      toast.success(`Booking ${status === "confirmed" ? "dikonfirmasi" : "ditolak"}`);
      load();
    } catch { toast.error("Gagal update"); }
  };

  const filtered = filter === "all" ? items : items.filter(i => i.status === filter);
  const counts = { all: items.length, pending: items.filter(i => i.status === "pending").length, confirmed: items.filter(i => i.status === "confirmed").length };

  return (
    <div data-testid="owner-bookings">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Inbox Reservations</p>
        <h1 className="font-heading text-3xl font-black mt-2">Booking Masuk</h1>
        <p className="text-muted-foreground mt-2">{counts.pending} pending • {counts.confirmed} confirmed</p>
      </div>

      <div className="flex gap-2 mb-6">
        {["all", "pending", "confirmed", "rejected", "cancelled"].map(s => (
          <Badge
            key={s}
            onClick={() => setFilter(s)}
            variant={filter === s ? "default" : "secondary"}
            className="cursor-pointer py-1.5 px-3"
            data-testid={`filter-${s}`}
          >
            {s === "all" ? "Semua" : STATUS_LABEL[s] || s}
          </Badge>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="warm-card p-12 text-center">
          <p className="text-muted-foreground">Belum ada booking.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => (
            <div key={b.id} className="warm-card p-5" data-testid={`owner-booking-${b.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-heading font-bold">{b.name} <span className="text-xs text-muted-foreground font-normal">@ {b.restaurant?.name}</span></div>
                  <div className="text-xs text-muted-foreground">{b.phone} • {b.user?.email}</div>
                </div>
                <Badge className={STATUS_COLORS[b.status]}>{STATUS_LABEL[b.status]}</Badge>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-primary" />{b.date}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-primary" />{b.time}</span>
                <span className="flex items-center gap-1"><Users className="w-4 h-4 text-primary" />{b.guests} orang</span>
              </div>
              {b.notes && <p className="text-sm mt-2 italic text-muted-foreground">"{b.notes}"</p>}
              {b.status === "pending" && (
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => updateStatus(b.id, "confirmed")} className="rounded-full bg-green-600 hover:bg-green-700" data-testid={`confirm-${b.id}`}>
                    Konfirmasi
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "rejected", "Maaf, slot penuh")} className="rounded-full" data-testid={`reject-${b.id}`}>
                    Tolak
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
